import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

import usePlgSelfServe from "hooks/usePlgSelfServe";
import useTalentApi from "components/Screens/Marketplace/Customer/TalentApiContext";

import PreferencePopup from "components/Screens/Marketplace/Customer/Shortlist/PreferencePopup";
import { configTiles, preferenceSubtitle, preferenceTitle } from "components/Screens/Marketplace/Customer/utils/preferenceTiles";
import usePlgSignupConfig from "plg/screens/Signup/usePlgSignupConfig";
import type { PreferenceTileView } from "components/Screens/Marketplace/Customer/utils/preferenceTiles";
import {
  loadAskState,
  nextPopupAsk,
  saveAskState,
  withAnswer,
  withAsk,
} from "components/Screens/Marketplace/Customer/Shortlist/preferenceAskStore";
import type { AskState } from "components/Screens/Marketplace/Customer/Shortlist/preferenceAskStore";
import type { ShortlistItem } from "components/Screens/Marketplace/Customer/types";
import { trackPlg } from "plg/analytics/events";
import { plgToolMatch } from "plg/analytics/toolMatch";
import { shortlistFiltersFor } from "components/Screens/Marketplace/Customer/Shortlist/shortlistFilters";

export const SHORTLIST_CAP = 3;
const ASK_DELAY_MS = 450;
// PLG asks 1 and 2 follow a save, so they are about the person just shortlisted (product 2026-10-31)
const ABOUT_SUB = "Tell us what made you shortlist them, and we will look for the same in others.";

export interface ShortlistContextValue {
  enabled: boolean;
  loading: boolean;
  items: ShortlistItem[];
  count: number;
  /** null = no limit. */
  cap: number | null;
  isFull: boolean;
  isShortlisted: (talentId: string) => boolean;
  /** `from`: where the save was clicked (shortlist_added.from). */
  add: (talentId: string, from?: "card" | "profile") => Promise<{ ok: true } | { ok: false; code?: string }>;
  remove: (talentId: string) => Promise<void>;
  refresh: () => Promise<void>;
  askState: AskState;
  updateAskState: (next: AskState) => void;
}

const EMPTY_ASK: AskState = { answered: false, asks: {}, stated: [] };
const noop = async () => {};
const ShortlistContext = createContext<ShortlistContextValue>({
  enabled: false,
  loading: false,
  items: [],
  count: 0,
  cap: SHORTLIST_CAP,
  isFull: false,
  isShortlisted: () => false,
  add: async () => ({ ok: false }),
  remove: noop,
  refresh: noop,
  askState: EMPTY_ASK,
  updateAskState: () => {},
});

export const useShortlist = () => useContext(ShortlistContext);

/** For a preview drawn with real cards in a set state (the What happens next? tour), without touching the shortlist. */
export const ShortlistPreviewProvider = ShortlistContext.Provider;

interface Popup {
  ask: 1 | 2;
  title: string;
  sub: string;
  tiles: PreferenceTileView[];
  personIds: string[];
}

export function ShortlistProvider({ children }: { children: ReactNode }) {
  const flag = usePlgSelfServe();
  const api = useTalentApi();
  const customerId = api.user?.customerId || "";
  const enabled =
    flag && !!customerId && api.hasPermission("talentNetwork.shortlist") && (api.kind === "plg" || api.user?.type === "customer");

  const [items, setItems] = useState<ShortlistItem[]>([]);
  const [cap, setCap] = useState<number | null>(SHORTLIST_CAP);
  const [loading, setLoading] = useState(false);
  const [askState, setAskState] = useState<AskState>(() => (customerId ? loadAskState(customerId) : EMPTY_ASK));
  const [popup, setPopup] = useState<Popup | null>(null);
  const { config } = usePlgSignupConfig();
  const inFlight = useRef(new Set<string>());

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const res = await api.fetchShortlist();
      setItems(res.items || []);
      // null = no limit; a missing value (older server) keeps the old cap of 3.
      setCap(res.cap === null ? null : res.cap || SHORTLIST_CAP);
    } catch {
      // keep the last known state; buttons re-enable and the next action refreshes again
    } finally {
      setLoading(false);
    }
  }, [enabled, api]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const updateAskState = useCallback(
    (next: AskState) => {
      setAskState(next);
      if (customerId) saveAskState(customerId, next);
    },
    [customerId]
  );

  const plg = api.kind === "plg";

  const maybeAsk = useCallback(
    async (count: number, talentId: string, currentIds: string[], firstName?: string) => {
      const ask = nextPopupAsk(askState, count);
      if (!ask) return;
      // One fixed tile set from PLG_SIGNUP_CONFIG.preferences, the same for every ask.
      const tiles = configTiles(config);
      if (!tiles.length) return;
      updateAskState(withAsk(askState, ask, "shown"));
      const ids = ask === 1 ? [talentId] : currentIds;
      const about = plg && firstName;
      setTimeout(() => {
        setPopup({
          ask,
          title: about ? `What did you like about ${firstName}?` : preferenceTitle(config),
          sub: about ? ABOUT_SUB : preferenceSubtitle(config, ask === 1 ? "ask1" : "ask2", count),
          tiles,
          personIds: ids,
        });
      }, ASK_DELAY_MS);
    },
    [askState, updateAskState, config, plg]
  );

  const add = useCallback(
    async (talentId: string, from: "card" | "profile" = "card") => {
      if (!enabled || inFlight.current.has(talentId)) return { ok: false as const };
      inFlight.current.add(talentId);
      try {
        const res = await api.addToShortlist(talentId, shortlistFiltersFor(talentId, from));
        if (res.ok === false) {
          await refresh(); // SHORTLIST_FULL / ALREADY_* — show the server truth
          return { ok: false as const, code: res.code };
        }
        // Analytics must never break the save flow.
        Promise.resolve(api.ingestAnalyticsEvent({ eventType: "SHORTLIST_ADDED", talentId, source: "PORTAL" })).catch(() => {});
        const fresh = await api.fetchShortlist().catch(() => null);
        const nextItems = fresh?.items ?? [...items, { talent: { _id: talentId } as any, addedAt: new Date().toISOString() }];
        setItems(nextItems);
        const saved = nextItems.find((i) => i.talent._id === talentId)?.talent;
        if (plg) {
          const talentTools = saved?.tools;
          plgToolMatch(talentTools).then((tool_match) =>
            trackPlg("shortlist_added", { talent_id: talentId, shortlist_count_after: nextItems.length, from, tool_match })
          );
        }
        maybeAsk(nextItems.length, talentId, nextItems.map((i) => i.talent._id), saved?.firstName);
        return { ok: true as const };
      } finally {
        inFlight.current.delete(talentId);
      }
    },
    [enabled, items, refresh, maybeAsk, api, plg]
  );

  const remove = useCallback(
    async (talentId: string) => {
      if (!enabled || inFlight.current.has(talentId)) return;
      inFlight.current.add(talentId);
      try {
        await api.removeFromShortlist(talentId);
        // Analytics must never break the save flow.
        Promise.resolve(api.ingestAnalyticsEvent({ eventType: "SHORTLIST_REMOVED", talentId, source: "PORTAL" })).catch(() => {});
        if (plg) {
          const addedAt = Date.parse(items.find((i) => i.talent?._id === talentId)?.addedAt || "");
          trackPlg("shortlist_removed", {
            talent_id: talentId,
            shortlist_count_after: items.filter((i) => i.talent?._id !== talentId).length,
            ...(Number.isNaN(addedAt) ? {} : { time_held_ms: Math.max(0, Date.now() - addedAt) }),
          });
        }
      } catch {
        // fall through to refresh
      } finally {
        inFlight.current.delete(talentId);
        await refresh();
      }
    },
    [enabled, refresh, api, plg, items]
  );

  const closePopup = (picked: PreferenceTileView[]) => {
    if (!popup) return;
    if (plg) {
      trackPlg("preference_answered", {
        tiles: picked.map((t) => t.cat),
        trigger: popup.ask === 1 ? "first_shortlist" : "second_shortlist",
        dismissed: !picked.length,
      });
    }
    if (picked.length) {
      const tiles = picked.map(({ cat, label }) => ({ cat, label }));
      updateAskState(withAnswer(askState, tiles));
      Promise.resolve(api.postPreferences({ source: popup.ask === 1 ? "ask1" : "ask2", tiles, personIds: popup.personIds })).catch(() => {});
    } else {
      updateAskState(withAsk(askState, popup.ask, "dismissed"));
    }
    setPopup(null);
  };

  const value = useMemo<ShortlistContextValue>(
    () => ({
      enabled,
      loading,
      items,
      count: items.length,
      cap,
      isFull: cap !== null && items.length >= cap,
      isShortlisted: (id) => items.some((i) => i.talent?._id === id),
      add,
      remove,
      refresh,
      askState,
      updateAskState,
    }),
    [enabled, loading, items, cap, add, remove, refresh, askState, updateAskState]
  );

  return (
    <ShortlistContext.Provider value={value}>
      {children}
      {popup && <PreferencePopup title={popup.title} sub={popup.sub} tiles={popup.tiles} onClose={closePopup} />}
    </ShortlistContext.Provider>
  );
}
